// Zod DTOs for the `/v1/me/*` student read surface (P3-06, spec §6.3), on
// top of P0-11's `entitlementSummarySchema`/`entitlementLevelSchema`
// (`./auth.ts`) which already cover `GET /v1/me/entitlements` + `GET
// /v1/me/levels`. This file adds the REST of §6.3's read-only surface:
// `/v1/me/levels/:id/subjects`, `/v1/me/subjects/:id/contents` (the pruned
// contents tree + per-tab counts), `/v1/me/sections/:id`, `/v1/me/sections/
// :id/tabs/:tabId`, and `/v1/me/resources/:id` — plus the ONE student write,
// `POST /v1/me/resources/:id/access-url`.
//
// **FROZEN DTO shapes** (this ticket's own AC / ADR "Frozen forward
// contracts" #5) — every shape below is deliberately a NARROWER projection
// of the authoring-side rows (`../../apps/api/src/services/{subjects,
// sections,tabs,resources}.ts`'s own `to*Response` mappers), never a 1:1 of
// the authoring response: a student never needs/should see
// `createdById`/raw `isPublished`/`publishedAt` bookkeeping — every node
// returned here is, by construction, already visible (§4's fail-closed
// filter already ran before any of these rows reached the wire).
import { z } from 'zod';
import { ResourceType, Role, TabKind } from '../enums';
import { emailSchema, idSchema, nameSchema, opaqueTokenSchema, passwordSchema } from './primitives';
import { sectionKindEnumSchema } from './section';
import { sectionLabelsSchema } from './level';

/** Local copy of `./auth.ts`'s (unexported) `roleEnumSchema` — same
 * `Object.values(Role)` cast, kept file-local rather than exported from
 * `./auth.ts` to avoid widening that file's surface for one cross-file
 * import. */
const roleEnumSchema = z.enum(Object.values(Role) as [Role, ...Role[]]);

const tabKindEnumSchema = z.enum(Object.values(TabKind) as [TabKind, ...TabKind[]]);
const resourceTypeEnumSchema = z.enum(
  Object.values(ResourceType) as [ResourceType, ...ResourceType[]],
);

// ─── Friendly-URL ancestry (docs/ADR-friendly-urls.md §5) ───────────────────
// Additive-only fields threaded onto the three response shapes below so a
// slug-addressed deep link (or a legacy cuid link) can render a breadcrumb
// without a second round trip. `type` mirrors the PascalCase model-name
// convention already used elsewhere for a discriminator over these four
// entities (e.g. `AuditLog.entityType`) — never invented fresh here.

const ancestorEntityTypeEnumSchema = z.enum(['ExamBoard', 'Level', 'Subject', 'Section']);
export type AncestorEntityType = z.infer<typeof ancestorEntityTypeEnumSchema>;

export const meAncestorSchema = z.object({
  type: ancestorEntityTypeEnumSchema,
  id: idSchema,
  name: z.string(),
  slug: z.string(),
});
export type MeAncestor = z.infer<typeof meAncestorSchema>;

/** Shared additive shape, spread onto every response below: `subjectName`/
 * `subjectSlug`, `levelName`/`levelSlug`, `boardName`/`boardSlug`, and the
 * ordered `ancestors` chain from board down to the entity's immediate
 * parent. Purely additive — no existing field removed/renamed/retyped. */
const meFriendlyUrlAncestryFields = {
  subjectName: z.string(),
  subjectSlug: z.string(),
  levelName: z.string(),
  levelSlug: z.string(),
  boardName: z.string(),
  boardSlug: z.string(),
  ancestors: z.array(meAncestorSchema),
};

// ─── GET /v1/me/levels/:id/subjects ─────────────────────────────────────────

export const meSubjectSchema = z.object({
  id: idSchema,
  levelId: idSchema,
  name: z.string(),
  slug: z.string(),
  position: z.number().int(),
  // Friendly-URL ancestry (docs/ADR-friendly-urls.md §5), additive-only —
  // lets a dashboard subject-card link straight to the friendly
  // `/app/learn/:boardSlug/:levelSlug/:subjectSlug` URL at click-time
  // instead of the legacy id route + client-side `router.replace()` hop.
  // `subjectSlug` mirrors `slug` (kept as its own field so callers building
  // the 3-segment friendly path can destructure `boardSlug`/`levelSlug`/
  // `subjectSlug` uniformly, matching `meFriendlyUrlAncestryFields` below).
  subjectSlug: z.string(),
  levelSlug: z.string(),
  levelName: z.string(),
  boardSlug: z.string(),
  boardName: z.string(),
});
export type MeSubject = z.infer<typeof meSubjectSchema>;

export const meLevelSubjectsResponseSchema = z.object({
  subjects: z.array(meSubjectSchema),
});
export type MeLevelSubjectsResponse = z.infer<typeof meLevelSubjectsResponseSchema>;

// ─── GET /v1/me/subjects (optional ?levelId=) ───────────────────────────────
// Single-call replacement for the client's former "probe `/levels/:id/
// subjects` once per entitled level until one 200s" pattern (the dominant
// cause of the >5s `/app/learn` slug-resolve load — D1 isn't in APAC, each
// probe cost ~1.2-1.4s). `levelId` is OPTIONAL here (unlike the path param on
// `/levels/:id/subjects`, which stays required) — omitted, the response is
// the UNION of subjects across every level the caller is entitled to, in ONE
// `AccessService.resolveEffectiveAccess` call instead of one per candidate
// level. Provided, behaves identically to `/levels/:id/subjects` (same
// 403/404 visibility rules) — kept so a caller that already knows the level
// id doesn't have to give that up. Response shape is the exact same
// `MeLevelSubjectsResponse` — no new DTO needed.
export const meSubjectsQuerySchema = z.object({
  levelId: idSchema.optional(),
});
export type MeSubjectsQuery = z.infer<typeof meSubjectsQuerySchema>;

// ─── GET /v1/me/subjects/:id/contents ───────────────────────────────────────
// "the contents table — pruned section tree with counts per tab" (spec
// §6.3). `tabCounts` is keyed by `TabKind`, one entry per tab KIND that has
// at least one visible (published) tab of that kind directly on this
// section, valued at the count of currently-visible (published) resources
// filed under it — a UI badge reads this directly without a second
// per-section fetch.

export interface MeContentsSectionNode {
  id: string;
  parentId: string | null;
  kind: z.infer<typeof sectionKindEnumSchema>;
  title: string;
  slug: string;
  position: number;
  depth: number;
  tabCounts: Record<string, number>;
  children: MeContentsSectionNode[];
}

export const meContentsSectionNodeSchema: z.ZodType<MeContentsSectionNode> = z.lazy(() =>
  z.object({
    id: idSchema,
    parentId: idSchema.nullable(),
    kind: sectionKindEnumSchema,
    title: z.string(),
    slug: z.string(),
    position: z.number().int(),
    depth: z.number().int().min(0).max(2),
    tabCounts: z.record(z.string(), z.number().int()),
    children: z.array(meContentsSectionNodeSchema),
  }),
);

export const meSubjectContentsResponseSchema = z.object({
  subjectId: idSchema,
  levelId: idSchema,
  sections: z.array(meContentsSectionNodeSchema),
  // The level's depth->noun labels (spec §3.5, same `sectionLabelsSchema`
  // the teacher-facing `GET /v1/levels/:id` surface already returns) — a
  // student client uses this to render "Unit"/"Chapter"/etc. per the
  // level's ACTUAL configured labels instead of a hardcoded/fallback noun
  // (see `apps/web/src/views/learn/ContentsTableView.vue`'s
  // `resolveSectionNoun`). Additive-only; falls back client-side to
  // `DEFAULT_SECTION_NOUNS` when a level has none of its three depths set.
  sectionLabels: sectionLabelsSchema,
  ...meFriendlyUrlAncestryFields,
});
export type MeSubjectContentsResponse = z.infer<typeof meSubjectContentsResponseSchema>;

// ─── GET /v1/me/sections/:id ────────────────────────────────────────────────
// "section meta + visible tabs" (spec §6.3).

export const meSectionTabSummarySchema = z.object({
  id: idSchema,
  kind: tabKindEnumSchema,
  label: z.string(),
  position: z.number().int(),
  allowDownload: z.boolean(),
});
export type MeSectionTabSummary = z.infer<typeof meSectionTabSummarySchema>;

export const meSectionResponseSchema = z.object({
  id: idSchema,
  subjectId: idSchema,
  levelId: idSchema,
  parentId: idSchema.nullable(),
  kind: sectionKindEnumSchema,
  title: z.string(),
  slug: z.string(),
  summary: z.string().nullable(),
  position: z.number().int(),
  depth: z.number().int().min(0).max(2),
  tabs: z.array(meSectionTabSummarySchema),
  ...meFriendlyUrlAncestryFields,
});
export type MeSectionResponse = z.infer<typeof meSectionResponseSchema>;

// ─── GET /v1/me/sections/:id/tabs/:tabId ────────────────────────────────────
// "resources within a tab" (spec §6.3).

export const meResourceSummarySchema = z.object({
  id: idSchema,
  tabId: idSchema,
  type: resourceTypeEnumSchema,
  subtype: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  position: z.number().int(),
  isFreePreview: z.boolean(),
  /** Type-discriminated per §3.4 — see `../../apps/api/src/services/
   * resources.ts`'s `ResourceResponse.payload` header for why this stays a
   * generic record rather than a narrowed-per-`type` union at this layer. */
  payload: z.record(z.string(), z.unknown()),
  assetId: idSchema.nullable(),
  durationSec: z.number().int().nullable(),
});
export type MeResourceSummary = z.infer<typeof meResourceSummarySchema>;

export const meSectionTabResponseSchema = z.object({
  id: idSchema,
  sectionId: idSchema,
  kind: tabKindEnumSchema,
  label: z.string(),
  position: z.number().int(),
  allowDownload: z.boolean(),
  resources: z.array(meResourceSummarySchema),
});
export type MeSectionTabResponse = z.infer<typeof meSectionTabResponseSchema>;

// ─── GET /v1/me/sections/:id/reader ─────────────────────────────────────────
// `docs/ADR-chapter-access-performance.md` §2.1 — collapses the "section
// then N per-tab" waterfall into one additive, composed response. Every
// field is the EXISTING `meSectionResponseSchema`/`meSectionTabSummarySchema`/
// `meResourceSummarySchema` shape, unchanged; the only new thing is each tab
// entry carrying its own `resources`, inlined subject to the ADR §2.3
// 150-resource server-side budget. `resources: null` means "not inlined —
// fetch lazily via the existing `GET /v1/me/sections/:id/tabs/:tabId`";
// `resources: []` keeps its existing meaning (a visible tab with zero
// visible resources) — the two must never be conflated.

export const meSectionReaderTabSchema = meSectionTabSummarySchema.extend({
  resources: z.array(meResourceSummarySchema).nullable(),
});
export type MeSectionReaderTab = z.infer<typeof meSectionReaderTabSchema>;

export const meSectionReaderResponseSchema = meSectionResponseSchema.extend({
  tabs: z.array(meSectionReaderTabSchema),
});
export type MeSectionReaderResponse = z.infer<typeof meSectionReaderResponseSchema>;

// Note: `GET /v1/me/resources/:id` and its response DTO live on
// `./me-resource.ts` (`meResourceDetailSchema`, `MeResourceService.getDetail`)
// per P3-22's reconciliation — this file no longer duplicates that shape.

// ─── POST /v1/me/resources/:id/access-url ───────────────────────────────────
// The ONE student write (spec §6.3) — mints a short-lived (<=300s, spec
// §7.2's TTL) signed GET/manifest for the resource. Response shape is the
// discriminated union `meResourceAccessUrlResponseSchema` on `./me-resource.ts`
// (`{kind:'asset',...} | {kind:'hls',...}`), not duplicated here.

export const meResourceAccessUrlRequestSchema = z.object({
  disposition: z.enum(['inline', 'attachment']).default('inline'),
});
export type MeResourceAccessUrlRequest = z.infer<typeof meResourceAccessUrlRequestSchema>;

// ─── P4-11 Self-service account profile ─────────────────────────────────────
// `GET/PATCH /v1/me/profile`, `POST /v1/me/avatar`, `PATCH /v1/me/password`,
// `POST /v1/me/password/set/request` + `/set/confirm` (Google-only step-up).
// See `apps/api/src/routes/me.ts`'s own section header for the full
// contract/rationale.

/**
 * `GET /v1/me/profile` response. `email`/`role`/`createdAt` are explicitly
 * READ-ONLY (no corresponding field ever appears in
 * `meProfileUpdateRequestSchema` below) — `role` in particular must never be
 * settable from a client body, see that schema's own doc comment. `avatarUrl`
 * is COMPOSED at response time (short-lived signed GET, same mechanism
 * `POST /v1/assets/:id/download-url` already uses) from the stored
 * `avatarKey` — never the stored key itself, and never a stored absolute
 * URL (see `prisma/schema.prisma`'s `User.avatarKey` doc comment).
 */
export const meProfileResponseSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  avatarUrl: z.string().nullable(),
  role: roleEnumSchema,
  createdAt: z.string().datetime(),
});
export type MeProfileResponse = z.infer<typeof meProfileResponseSchema>;

/**
 * `PATCH /v1/me/profile` request — `name` ONLY. This schema is the
 * enforcement point for "role is never settable by the client": it is a
 * zod `.strict()`-free but EXPLICIT allowlist object (no `role`/`email`/
 * `avatarKey`/etc. key exists on it at all), so `@hono/zod-validator`'s
 * `c.req.valid('json')` can never yield a `role` field even if a caller's
 * raw request body contains one — the extra key is silently dropped by
 * zod's default (non-strict) object parsing, never read, never passed to
 * `UserRepository`. See `apps/api/test/routes/me.profile.test.ts`'s
 * "role-escalation attempt" case for the live proof.
 */
export const meProfileUpdateRequestSchema = z.object({
  name: nameSchema,
});
export type MeProfileUpdateRequest = z.infer<typeof meProfileUpdateRequestSchema>;

/** `POST /v1/me/avatar` response — the composed, short-lived signed URL
 * (never the raw storage key). */
export const meAvatarUploadResponseSchema = z.object({
  avatarUrl: z.string(),
});
export type MeAvatarUploadResponse = z.infer<typeof meAvatarUploadResponseSchema>;

/** `DELETE /v1/me/avatar` response — always `{ avatarUrl: null }` (idempotent:
 * both "an avatar was removed" and "there was never one to remove" end in
 * the same post-condition, so both return this same shape/status). */
export const meAvatarDeleteResponseSchema = z.object({
  avatarUrl: z.null(),
});
export type MeAvatarDeleteResponse = z.infer<typeof meAvatarDeleteResponseSchema>;

/**
 * `PATCH /v1/me/password` request. `currentPassword` is REQUIRED by this
 * schema unconditionally — the handler branches at runtime on whether
 * `User.passwordHash` is actually set (normal case: verify it via
 * `verifyPassword`; Google-only case: `currentPassword` is ignored and the
 * step-up `VerificationToken` flow gates the write instead — see
 * `me.ts`'s header). Kept as one shared request shape (rather than two
 * schemas) since the wire contract is identical either way; only the
 * server-side branch differs.
 */
export const mePasswordChangeRequestSchema = z.object({
  currentPassword: z.string().min(1, 'currentPassword is required'),
  newPassword: passwordSchema,
});
export type MePasswordChangeRequest = z.infer<typeof mePasswordChangeRequestSchema>;

/** `POST /v1/me/password/set/request` — no body; the target mailbox is
 * always the CALLER's own verified `User.email` (never a client-supplied
 * address), taken from the auth context server-side. */
export const meSetPasswordConfirmRequestSchema = z.object({
  token: opaqueTokenSchema,
  newPassword: passwordSchema,
});
export type MeSetPasswordConfirmRequest = z.infer<typeof meSetPasswordConfirmRequestSchema>;
